import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { Add, Delete, Edit } from '@material-ui/icons';
import { toast } from 'react-toastify';

import {
  addQuesModule,
  deleteQuesModule,
  getAllQuesModules,
  updateQuesModule,
} from '../../../services/apiService';
import { setQuestionLevels } from '../../../redux/reducers/questionLevelsReducer';
import { COURSE_TYPE, COURSE_TYPE_NAMES_MAPPING } from '../../../constants';

class LevelBuilder extends Component {
  constructor() {
    super();
    this.state = {
      newModule: null,
      editModule: null,
      deleteModule: null,
      isLoading: false,
      validationError: null,
    };
  }

  componentDidMount = async () => {
    try {
      this.props.dispatch(setQuestionLevels(await getAllQuesModules()));
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch data, Please try again later');
    }
  };

  addNewModuleHandler = async (e) => {
    try {
      e.preventDefault();
      this.setState({ isLoading: true });
      const newModule = await addQuesModule(this.state.newModule);
      this.setState({
        newModule: null,
        editModule: { ...newModule, Competencies: [] },
        isLoading: false,
      });
      this.componentDidMount();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add new module, Please try again later');
      this.setState({ isLoading: false });
    }
  };

  isInputValid = () => {
    const mdl = this.state.editModule;
    let validationError = null;
    if (!mdl.name) validationError = 'mdl_name';
    if (!mdl.courseType) validationError = 'ct_name';
    mdl.Competencies.forEach((cmp, i) => {
      if (!cmp.name) validationError = `cmp_name_${i}`;
      if (!cmp.ratingTemplateId) validationError = `cmp_rt_${i}`;
      cmp.Concepts.forEach((cpt, j) => {
        if (!cpt.name) validationError = `cpt_name_${i}_${j}`;
      });
    });

    if (validationError) {
      this.setState({ validationError });
      return false;
    }
    return true;
  };

  updateModuleHandler = async () => {
    try {
      if (!this.isInputValid()) return;
      this.setState({ isLoading: true });
      await updateQuesModule(this.state.editModule);
      toast.info('Changes saved!');
      this.setState({ editModule: null, isLoading: false });
    } catch (err) {
      console.error(err);
      toast.error('Failed to update module, Please try again later');
      this.setState({ isLoading: false });
    }
  };

  deleteModuleHandler = async () => {
    try {
      this.setState({ isLoading: true });
      await deleteQuesModule(this.state.deleteModule);
      toast.info('Module Deleted!');
      this.setState({ deleteModule: null, isLoading: false });
      this.componentDidMount();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete module, Please try again later');
      this.setState({ isLoading: false });
    }
  };

  // view for adding new module
  render = () => {
    if (this.state.newModule)
      return (
        <div className='bg-white p-8'>
          <h2 className='text-xl font-medium opacity-80 mb-8'>
            Add New Module
          </h2>

          <form
            onSubmit={this.addNewModuleHandler}
            className='flex flex-col gap-4 max-w-2xl'
          >
            <TextField
              onChange={(e) =>
                this.setState({ newModule: { name: e.target.value } })
              }
              value={this.state.newModule?.name || ''}
              variant='outlined'
              label='Module Name'
              autoFocus
              required
            />

            <div className='text-right'>
              <button
                type='reset'
                onClick={() => this.setState({ newModule: null })}
                className='opacity-60 font-medium w-40'
              >
                Cancel
              </button>
              <button
                type='submit'
                className='bg-blue-c1 text-white font-medium rounded py-2 w-40'
              >
                {this.state.isLoading ? (
                  <CircularProgress
                    color='inherit'
                    size='1.5rem'
                    className='-my-1.5'
                  />
                ) : (
                  <span>Confirm</span>
                )}
              </button>
            </div>
          </form>
        </div>
      );

    // view for editing module
    if (this.state.editModule)
      return (
        <div className='bg-white p-8'>
          <h2 className='text-xl font-medium opacity-80'>Edit Module</h2>

          <div className='max-w-xl my-4'>
            <TextField
              onChange={(e) => {
                const { editModule } = this.state;
                editModule.name = e.target.value;
                this.setState({ editModule });
              }}
              value={this.state.editModule.name}
              label='Module Name'
              error={this.state.validationError === 'mdl_name'}
              helperText={
                this.state.validationError === 'mdl_name' &&
                'Module name cannot be empty'
              }
              variant='outlined'
              fullWidth
            />
          </div>
          
          <div className='max-w-xl my-4'>
            <TextField
              onChange={(e) => {
                const { editModule } = this.state;
                editModule.courseType = e.target.value;
                this.setState({ editModule });
              }}
              value={this.state.editModule.courseType}
              label='Course Type'
              error={this.state.validationError === 'ct_name'}
              helperText={
                this.state.validationError === 'ct_name' &&
                'Choose Course Type'
              }
              variant='outlined'
              size='small'
              fullWidth
              select
            >
              {Object.values(COURSE_TYPE).map((courseType) => (
                <MenuItem value={courseType} key={courseType}>
                  {COURSE_TYPE_NAMES_MAPPING[courseType]}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className='mt-8'>
            {this.state.editModule.Competencies.map((competency, i) => (
              <div
                key={competency.id}
                className='grid grid-cols-2 items-baseline my-4'
              >
                <div className='bg-blue-c1 bg-opacity-10 rounded-l-lg p-4'>
                  <TextField
                    onChange={(e) => {
                      const { editModule } = this.state;
                      editModule.Competencies[i].name = e.target.value;
                      this.setState({ editModule });
                    }}
                    value={competency.name}
                    label={`Competency ${i + 1}`}
                    error={this.state.validationError === `cmp_name_${i}`}
                    helperText={
                      this.state.validationError === `cmp_name_${i}` &&
                      'Competency name cannot be empty'
                    }
                    variant='outlined'
                    fullWidth
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => {
                            const { editModule } = this.state;
                            editModule.Competencies.splice(i, 1);
                            this.setState({ editModule });
                          }}
                        >
                          <Delete />
                        </IconButton>
                      ),
                    }}
                  />
                </div>

                <div className='bg-blue-c1 bg-opacity-10 rounded-r-lg rounded-bl-lg p-4 flex flex-col gap-4'>
                  <div>
                    <TextField
                      select
                      onChange={(e) => {
                        const { editModule } = this.state;
                        editModule.Competencies[i].ratingTemplateId =
                          e.target.value;
                        this.setState({ editModule });
                      }}
                      value={competency.ratingTemplateId}
                      label='Rating Template For Competency'
                      variant='outlined'
                      size='small'
                      error={this.state.validationError === `cmp_rt_${i}`}
                      helperText={
                        this.state.validationError === `cmp_rt_${i}` &&
                        'Rating Template cannot be empty'
                      }
                      fullWidth
                    >
                      {this.props.ratingTemplates?.map((rt) => (
                        <MenuItem value={rt.id} key={rt.id}>
                          {rt.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  {competency.Concepts.map((concept, j) => (
                    <div key={concept.id}>
                      <TextField
                        onChange={(e) => {
                          const { editModule } = this.state;
                          editModule.Competencies[i].Concepts[j].name =
                            e.target.value;
                          this.setState({ editModule });
                        }}
                        value={concept.name}
                        label={`Concept ${j + 1}`}
                        variant='outlined'
                        size='small'
                        fullWidth
                        autoFocus
                        error={
                          this.state.validationError === `cpt_name_${i}_${j}`
                        }
                        helperText={
                          this.state.validationError === `cpt_name_${i}_${j}` &&
                          'Concept name cannot be empty'
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => {
                                const { editModule } = this.state;
                                editModule.Competencies[i].Concepts.splice(
                                  j,
                                  1
                                );
                                this.setState({ editModule });
                              }}
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          ),
                        }}
                      />
                    </div>
                  ))}

                  <button
                    onClick={() => {
                      const { editModule } = this.state;
                      editModule.Competencies[i].Concepts.push({
                        name: '',
                        competencyId: competency.id,
                      });
                      this.setState({ editModule });
                    }}
                    className='text-blue-c1 flex items-center gap-1'
                  >
                    <Add />
                    <span>Add Concept</span>
                  </button>
                </div>
              </div>
            ))}

            <button
              onClick={() => {
                const { editModule } = this.state;
                editModule.Competencies.push({
                  name: '',
                  Concepts: [],
                });
                this.setState({ editModule });
              }}
              className='text-blue-c1 flex items-center gap-1 mt-4 ml-2'
            >
              <Add />
              <span>Add Competency</span>
            </button>

            <button
              onClick={this.updateModuleHandler}
              className='bg-blue-c1 text-white font-medium rounded py-2 w-64 mt-8 mr-4'
            >
              {this.state.isLoading ? (
                <CircularProgress
                  color='inherit'
                  size='1.5rem'
                  className='-my-1.5'
                />
              ) : (
                <span>Save</span>
              )}
            </button>

            <button
              onClick={() =>
                this.setState({ editModule: null, validationError: null })
              }
              className='opacity-40 font-medium ring-1 ring-black ring-opacity-40 rounded py-2 w-64'
            >
              Close
            </button>
          </div>
        </div>
      );

    // default view
    return this.props.allModulesfetchReady? (
      <div className='bg-white p-4'>
        <div className='flex gap-8 m-2'>
          <h2 className='text-2xl font-medium opacity-80'>{
          this.props.allModules.length===0?"No Modules Found...":"Modules"}</h2>

          <button
            onClick={() => this.setState({ newModule: { name: '' } })}
            className='border border-blue-c1 border-opacity-60 text-blue-c1 text-sm rounded-md px-3 py-1'
          >
            Add New Module
          </button>
        </div>

        <div className='grid grid-cols-2 gap-4 mt-8'>
          {this.props.allModules.map((mod) => (
            <div
              className='border-2 border-black border-opacity-10 rounded-lg px-4 py-3 flex items-center'
              key={mod.id}
            >
              <div className='flex-grow'>
                <h6 className='text-xl opacity-70 -mb-0.5'>{mod.name}</h6>
                <span className='text-xs opacity-40'>ID: {mod.id}</span>
              </div>

              <IconButton
                onClick={() => this.setState({ editModule: mod })}
                className='opacity-60 hover:opacity-100 hover:text-blue-c1'
              >
                <Edit />
              </IconButton>

              <span className='w-0.5 bg-black bg-opacity-10 my-2 mx-1 self-stretch' />

              <IconButton
                onClick={() => this.setState({ deleteModule: mod })}
                className='opacity-60 hover:opacity-100 hover:text-red-500'
              >
                <Delete />
              </IconButton>
            </div>
          ))}
        </div>

        {this.state.deleteModule && (
          <div className='fixed inset-0 bg-black bg-opacity-25 flex'>
            <div className='m-auto bg-white rounded-lg text-center p-4'>
              <h6 className='text-2xl font-semibold opacity-80 pt-4'>
                Delete Module?
              </h6>
              <p className='text-lg font-medium opacity-60 p-4'>
                Are you sure want to delete `{this.state.deleteModule.name}` ?
              </p>

              <hr className='border-t border-black border-opacity-10 my-4' />

              <div className='text-right'>
                <button
                  onClick={() => this.setState({ deleteModule: null })}
                  disabled={this.state.isLoading}
                  className='opacity-50 font-medium w-32'
                >
                  Cancel
                </button>
                <button
                  onClick={this.deleteModuleHandler}
                  disabled={this.state.isLoading}
                  className='bg-red-c1 text-white font-medium rounded-md py-2 w-32'
                >
                  {this.state.isLoading ? (
                    <CircularProgress
                      color='inherit'
                      size='1.5rem'
                      className='-my-1.5'
                    />
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className='bg-white flex justify-center py-32'>
        <CircularProgress /> 
      </div>
    );
  };
}

const mapStateToProps = ({ questionLevelsReducer, ratingTemplateReducer }) => ({
  allModules: questionLevelsReducer.modules,
  allModulesfetchReady: questionLevelsReducer.fetchReady,
  ratingTemplates: ratingTemplateReducer.ratingTemplates,
});

export default connect(mapStateToProps)(LevelBuilder);
